import { useRef, useState, type ReactElement } from "react";
import { Button, ServerNotice, barFill, logical } from "../../ui/kit/index.ts";
import { EntryScreen } from "../entry-screen.tsx";
import type { MaintenanceCountdown } from "../hud/maintenance-warning.ts";
import { timeOfDay } from "../server-strip.tsx";
import { QUEUE_WORDING, queueEstimate, queueProgress, queueStrip, type QueueState } from "./queue-state.ts";

/**
 * The queue window (P1.4-T164; ADR-127 decisions 5, 8 and 11; the design's `QueueWindow`, a visual reference): what
 * the page shows from the first `queue` frame until its `welcome` or a close, in the entry screens' layout, the small
 * wordmark over a window 184 wide. The window holds the player's place as its big number under "Your place", the
 * estimate in coarse words beside the character's name and a bar of the places passed, with Leave queue across its
 * footer. The design's paragraph under the bar is not shown (the author's ruling of 2026-10-12), so the bar is the
 * body's last part. While the gateway has no estimate the window writes its sentence on the
 * estimate's line, alone, and never a number or a spinner: the sentence and the name do not fit one line, and a
 * second line would change the window's height as estimates come and go and push it off a 230 tall area under the
 * strip. A maintenance still to come shows as a strip over the window, as wide as it.
 *
 * It draws only what `queue-state.ts` works out, and redraws when a frame arrives, never on a timer.
 */

/**
 * The window's measurements in logical pixels, the design's: its width; the body's padding above and below, the gap
 * between its parts and between the lines of its head; and the bar, a track 160 by 4 with a 1 pixel edge.
 */
export const QUEUE_METRICS = { width: 184, paddingY: 2, gap: 4, headGap: 2, bar: { width: 160, height: 4, edge: 1 } } as const;

/**
 * What the queue screen shows and does: the window's state; the maintenance schedule the connection was sent, or
 * null; the character's name, empty when the page does not know it yet; what Leave queue does; and the time of day
 * of a page time, the player's own clock unless a test gives another.
 */
export interface QueueScreenProps {
  readonly queue: QueueState;
  readonly scheduled: MaintenanceCountdown | null;
  readonly name: string;
  readonly onLeave: () => void;
  readonly clock?: (ms: number) => string;
}

/**
 * The bar of places passed: the fill cropped to whole logical pixels inside the track's edge (`barFill`), named
 * for assistive technology, with no label line of its own.
 *
 * @param props The places passed and the places there were.
 * @returns The bar.
 */
function QueueBar({ value, max }: { readonly value: number; readonly max: number }): ReactElement {
  const bar = QUEUE_METRICS.bar;
  const fill = barFill(value, max, bar.width - bar.edge - bar.edge);
  return (
    <div className="ot-queue__bar" role="progressbar" aria-label={QUEUE_WORDING.progress} aria-valuemin={0} aria-valuemax={max} aria-valuenow={value} data-queue="bar" data-fill={fill} style={{ width: logical(bar.width), height: logical(bar.height), borderWidth: logical(bar.edge) }}>
      <div className="ot-queue__fill" style={{ width: logical(fill) }} />
    </div>
  );
}

/**
 * The line under the place: the estimate and the character's name with a middle dot between them, as the design
 * writes it, or the estimate alone when the page does not know the name.
 *
 * @param estimate The estimate's words.
 * @param name The character's name, or empty.
 * @returns The line.
 */
export function waitLine(estimate: string, name: string): string {
  return name === "" ? estimate : `${estimate} · ${name}`;
}

/**
 * The queue screen.
 *
 * @param props The window's state, the maintenance schedule, the character's name and what Leave queue does.
 * @returns The screen.
 */
export function QueueScreen({ queue, scheduled, name, onLeave, clock = timeOfDay }: QueueScreenProps): ReactElement {
  const [leaving, setLeaving] = useState(false);
  const spent = useRef(false);
  const estimate = queueEstimate(queue.etaMs);
  const progress = queueProgress(queue);
  const strip = queueStrip(scheduled, queue.at, clock);
  const metrics = QUEUE_METRICS;
  /**
   * Leaves the queue once: the button is spent as it is pressed, so a second press does nothing. The press is
   * remembered at once, since the disabled button is drawn only after the task that pressed it, and a second press
   * in that task would otherwise reach the page too.
   */
  const leave = (): void => {
    if (spent.current) {
      return;
    }
    spent.current = true;
    setLeaving(true);
    onLeave();
  };
  return (
    <EntryScreen
      mode="queue"
      layout={`queue:${estimate === null ? "working" : "estimate"}:${strip === null ? "bare" : "strip"}`}
      title={QUEUE_WORDING.title}
      width={metrics.width}
      mark="small"
      over={strip === null ? null : <ServerNotice tone="soon" title={strip.title} text={strip.text} width={metrics.width} />}
      footer={
        <Button fullWidth disabled={leaving} onClick={leave}>
          {QUEUE_WORDING.leave}
        </Button>
      }
    >
      <div className="ot-queue" data-queue="window" style={{ gap: logical(metrics.gap), padding: `${logical(metrics.paddingY)} 0` }}>
        <div className="ot-queue__head" style={{ gap: logical(metrics.headGap) }}>
          <span className="ot-queue__label">{QUEUE_WORDING.place}</span>
          <span className="ot-queue__place" data-queue="place">
            {queue.place}
          </span>
          <span className="ot-queue__wait" data-queue="wait" aria-live="polite">
            {estimate === null ? QUEUE_WORDING.working : waitLine(estimate, name)}
          </span>
        </div>
        <QueueBar value={progress.value} max={progress.max} />
      </div>
    </EntryScreen>
  );
}
